import { useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { ReactComponent as GreenCheckCircleIcon } from "../../asserts/logo/GreenCheckCircle.svg";
import { ReactComponent as CloseToastIcon } from "../../asserts/logo/close.svg";

const TOAST_TEXT_SX = {
  fontFamily: "Lato, sans-serif",
  fontSize: 14,
  fontWeight: 400,
  fontStyle: "normal",
  lineHeight: "21px",
  letterSpacing: 0,
  color: "#E6E6E6",
};

/**
 * Figma toast (5400:29195) — bottom-left, success icon, dismiss, no CTA.
 */
export default function AppToast({
  open,
  title = "",
  subtitle = "",
  onClose,
  autoHideMs = 8000,
  width = 407,
  showDismiss = true,
  statusIcon,
}) {
  useEffect(() => {
    if (!open || !autoHideMs) return undefined;
    const t = setTimeout(() => onClose?.(), autoHideMs);
    return () => clearTimeout(t);
  }, [open, autoHideMs, onClose]);

  if (!open) return null;

  const heading = String(title || "").trim();
  const detail = String(subtitle || "").trim();
  if (!heading && !detail) return null;

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        position: "fixed",
        bottom: "24px",
        left: "24px",
        zIndex: 1600,
        boxSizing: "border-box",
        width,
        maxWidth: "calc(100vw - 48px)",
        minHeight: detail ? 95 : 0,
        p: "16px",
        display: "flex",
        alignItems: detail ? "flex-start" : "center",
        gap: "12px",
        bgcolor: "#000000",
        borderRadius: "4px",
        boxShadow: "0px 10px 10px 0px #0000003D, 0px 0px 10px 0px #2122261F",
      }}
    >
      <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", pt: detail ? "2px" : 0 }}>
        {statusIcon ?? <GreenCheckCircleIcon style={{ width: 24, height: 24, display: "block" }} />}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {heading ? <Typography sx={TOAST_TEXT_SX}>{heading}</Typography> : null}
        {detail ? (
          <Typography sx={{ ...TOAST_TEXT_SX, mt: heading ? "4px" : 0 }}>{detail}</Typography>
        ) : null}
      </Box>

      {showDismiss ? (
        <Box
          component="button"
          type="button"
          onClick={onClose}
          aria-label="Dismiss"
          sx={{
            flexShrink: 0,
            border: "none",
            bgcolor: "transparent",
            p: 0,
            m: 0,
            mt: detail ? "-2px" : 0,
            mr: "-2px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            opacity: 0.9,
            "&:hover": { opacity: 1 },
          }}
        >
          <CloseToastIcon style={{ width: 24, height: 24, display: "block" }} />
        </Box>
      ) : null}
    </Box>
  );
}
